import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

const genericRelationshipsUrl = (path?: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'generic-relationships' },
    op: 'query',
    resourcePath: 'generic-relationships',
  };
  const base = buildBaseURL(opts);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
};

const userRelationshipsUrl = (path?: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'user-relationships' },
    op: 'query',
    resourcePath: 'user-relationships',
  };
  const base = buildBaseURL(opts);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
};

export interface DelegateGovernorPayload {
  delegateGovernorId: string;
  delegateGovernorType: string;
  expiresDate: string;
  governorId: string;
  governorType: string;
}

/** POST /generic-relationships/delegate-governor */
export function delegateGovernorRelationshipRequest(payload: DelegateGovernorPayload): RequestInfo {
  const body = JSON.stringify({
    'delegate-governor-id': payload.delegateGovernorId,
    'delegate-governor-type': payload.delegateGovernorType,
    'delegate-expires-after': payload.expiresDate,
    'governor-id': payload.governorId,
    'governor-type': payload.governorType,
  });
  return {
    url: genericRelationshipsUrl('delegate-governor'),
    method: 'POST',
    body,
    cacheOptions: { [SkipCache]: true },
  };
}

export interface GrantGenericRelationshipPayload {
  governorId: string;
  governorType: string | undefined;
  governableId: string;
  governableType: string | undefined;
  relationshipType: string | undefined;
}

export interface GrantBulkGenericRelationshipPayload {
  governorId: string;
  governorType: string | undefined;
  governableData: { 'governable-id': string; 'governable-type': string }[];
  relationshipType: string | undefined;
}

/**
 * POST /generic-relationships/grant
 * body `{ 'governor-id', 'governor-type', 'governable-id', 'governable-type', 'relationship-type' }`
 *
 * Mirrors the ADMIN `EmployeeAdapter#grantGenericRelationship`, which built the
 * URL via `this.buildURL('generic-relationships') + '/grant'` and sent
 * `this.ajax(url, 'POST', { data: JSON.stringify({ 'governor-id': governorId, 'governor-type': governorType, 'governable-id': governableId, 'governable-type': governableType, 'relationship-type': relationshipType }) })`.
 * The adapter's `data` was already a JSON string (so the wire body is that
 * kebab-case object). The tio-employee employee adapter never defined this
 * method — admin-only surface. `JSON.stringify` drops any `undefined` value the
 * call site passes from an optional `selectedRole`, exactly as the adapter did.
 */
export function grantGenericRelationshipRequest(
  payload: GrantGenericRelationshipPayload
): RequestInfo {
  const body = JSON.stringify({
    'governor-id': payload.governorId,
    'governor-type': payload.governorType,
    'governable-id': payload.governableId,
    'governable-type': payload.governableType,
    'relationship-type': payload.relationshipType,
  });
  return {
    url: genericRelationshipsUrl('grant'),
    method: 'POST',
    body,
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /generic-relationships/grant-bulk
 * body `{ 'governor-id', 'governor-type', 'governable-data', 'relationship-type' }`
 *
 * Mirrors the ADMIN `EmployeeAdapter#grantBulkGenericRelationship`, which built
 * the URL via `this.buildURL('generic-relationships') + '/grant-bulk'` and sent
 * `this.ajax(url, 'POST', { data: JSON.stringify({ 'governor-id': governorId, 'governor-type': governorType, 'governable-data': governableData, 'relationship-type': relationshipType }) })`.
 * `governableData` is an array of `{ 'governable-id', 'governable-type' }` (kebab
 * keys built at the call site). Admin-only surface (no employee adapter method).
 */
export function grantBulkGenericRelationshipRequest(
  payload: GrantBulkGenericRelationshipPayload
): RequestInfo {
  const body = JSON.stringify({
    'governor-id': payload.governorId,
    'governor-type': payload.governorType,
    'governable-data': payload.governableData,
    'relationship-type': payload.relationshipType,
  });
  return {
    url: genericRelationshipsUrl('grant-bulk'),
    method: 'POST',
    body,
    cacheOptions: { [SkipCache]: true },
  };
}

/** DELETE /generic-relationships/:roleId/revoke */
export function deleteJurisdictiveRelationshipRequest(roleId: string): RequestInfo {
  return {
    url: genericRelationshipsUrl(`${roleId}/revoke`),
    method: 'DELETE',
    cacheOptions: { [SkipCache]: true },
  };
}

/** DELETE /user-relationships/:roleId/revoke */
export function revokeUserRelationshipRequest(roleId: string): RequestInfo {
  return {
    url: userRelationshipsUrl(`${roleId}/revoke`),
    method: 'DELETE',
    cacheOptions: { [SkipCache]: true },
  };
}
